import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';

const helper = new JwtHelperService();

/* State and Services */
import { AuthService } from '@core/services/auth.service';
import { AuthSelectors } from './auth.selectors';

/* Actions */
import {
	AutoLogin,
	DeactivateAuth,
	GenerateRecoveryCodes,
	Login,
	LoginWithRecoveryCode,
	Logout,
	SetupAuth,
	VerifyAuth,
} from '@core/store/auth/auth.action';

/* Models */
import { AuthStateModel } from '@core/store/auth/auth.model';
import { initialState } from '@core/store/auth/auth.model';
import { AuthObj } from '@core/models/user.model';
import { AppSelectors } from '../app/app.selectors';
import { AuthSettings } from '@core/models/tfa.model';
import { AppState } from '../app/app.state';
import { SelectControlValueAccessor } from '@angular/forms';

type LocalStateContext = StateContext<AuthStateModel>;

@State<AuthStateModel>({
	name: 'auth',
	defaults: initialState,
})
@Injectable()
export class AuthState {
	constructor(private authService: AuthService, private store: Store) {}

	@Selector()
	static token(state: AuthStateModel): string {
		return state.token;
	}
	@Selector()
	static isAdmin(state: AuthStateModel): boolean {
		return state.isAdmin;
	}
	@Selector()
	static authSettings(state: AuthStateModel): AuthSettings {
		return state.tfa;
	}
	@Selector()
	static isAuthenticated(state: AuthStateModel): boolean {
		if (!state.token) {
			return null;
		}

		return !helper.isTokenExpired(state.token);
	}
	@Selector()
	static loginInProgress(state: AuthStateModel): boolean {
		return state.loginCredentials !== undefined;
	}

	@Action(AutoLogin)
	async autoLogin({ getState, patchState }: LocalStateContext) {
		if (!this.store.selectSnapshot(AuthState.isAuthenticated)) {
			return;
		}

		try {
			const { token, isAdmin, tfa } = await this.authService.login({ token: getState().token }).toPromise();
			patchState({ token, isAdmin, tfa: { secret: tfa } });
		} catch (error) {
			throw error;
		}
	}

	@Action(Login)
	async login({ patchState, setState }: LocalStateContext, action: Login) {
		try {
			const { token, isAdmin, tfa } = await this.authService.login(action.authObj).toPromise();
			setState({ token, isAdmin, tfa: { secret: tfa } });
		} catch (error) {
			if (error === 'No Auth Code') {
				const { username, password } = action.authObj;
				patchState({ loginCredentials: { username, password } });
			}
			throw error;
		}
	}

	@Action(LoginWithRecoveryCode)
	async loginWithRecoveryCode({ setState, getState }: LocalStateContext, action: LoginWithRecoveryCode) {
		try {
			const loginCredentials = getState().loginCredentials;
			const { token, isAdmin, tfa } = await this.authService
				.loginWithRecoveryCode(action.recoveryCode, loginCredentials)
				.toPromise();
			setState({ token, isAdmin, tfa: { secret: tfa } });
		} catch (error) {
			throw error;
		}
	}

	@Action(Logout)
	logout({ setState }: LocalStateContext) {
		return this.authService.logout().pipe(tap(() => setState(initialState)));
	}

	@Action(SetupAuth)
	async setupAuth({ patchState }: LocalStateContext) {
		try {
			const username = this.store.selectSnapshot(AppState.user).username;
			const tfa = await this.authService.setupAuth(username).toPromise();
			patchState({ tfa });
		} catch (error) {
			throw error;
		}
	}

	@Action(VerifyAuth)
	async verifyAuth({ getState, patchState }: LocalStateContext, action: VerifyAuth) {
		try {
			const username = this.store.selectSnapshot(AppState.user).username;
			const tempSecret = getState().tfa.tempSecret;
			await this.authService.verifyAuth(username, tempSecret, action.authCode).toPromise();
			patchState({ tfa: { secret: tempSecret } });
		} catch (error) {
			throw error;
		}
	}
	@Action(DeactivateAuth)
	async deactivateAuth({ patchState }) {
		try {
			const username = this.store.selectSnapshot(AppState.user).username;
			await this.authService.deactivateAuth(username).toPromise();
			patchState({ tfa: null });
		} catch (error) {
			throw error;
		}
	}

	@Action(GenerateRecoveryCodes)
	async generateRecoveryCodes({ patchState }: LocalStateContext) {
		const userId = this.store.selectSnapshot(AppState.user).id;
		try {
			const recoveryCodes = await this.authService.generateRecoveryCodes(userId).toPromise();
			patchState({ recoveryCodes });
		} catch (error) {
			throw error;
		}
	}
}
